import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRightOnRectangleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";

const API = process.env.REACT_APP_API_URL;

const ROLE_NAMES = {
  1: "Administrator",
  2: "Teacher",
  3: "Student",
  4: "Guest",
};

// the token lives in localStorage either bare or already prefixed
const readToken = () => {
  const raw = localStorage.getItem("jstoken") || "";
  return raw.startsWith("Bearer") ? raw.split(" ")[1] : raw;
};

const readClaims = (token) => {
  try {
    const base = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(atob(base))));
  } catch (error) {
    return null;
  }
};

const Profile = () => {
  const history = useHistory();
  const token = readToken();
  const [claims, setClaims] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!token) {
      history.push("/login_register");
      return;
    }
    setClaims(readClaims(token));
  }, [token, history]);

  const submit = async (event) => {
    event.preventDefault();
    setNotice(null);
    if (password.length < 8) {
      setNotice({ ok: false, text: "Use at least 8 characters." });
      return;
    }
    if (password !== confirm) {
      setNotice({ ok: false, text: "The two passwords do not match." });
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API}/user/password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_password: password }),
      });
      if (response.ok) {
        setPassword("");
        setConfirm("");
        setNotice({
          ok: true,
          text: "Password changed. It is the one you sign in with from now on.",
        });
      } else {
        setNotice({
          ok: false,
          text: `The server refused the change (HTTP ${response.status}).`,
        });
      }
    } catch (error) {
      setNotice({ ok: false, text: "Could not reach the server." });
    } finally {
      setSaving(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem("jstoken");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_name");
    history.push("/");
  };

  const email = claims?.sub || "";
  const role = ROLE_NAMES[Number(localStorage.getItem("user_role"))] || "Student";
  const name = localStorage.getItem("user_name") || "Your account";

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <h1 className="text-3xl font-bold text-gray-900">Your account</h1>
      <p className="mt-2 text-lg text-gray-500">
        The details we hold for you, and where to change your password.
      </p>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
      >
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Name
            </dt>
            <dd className="mt-1 text-gray-900">{name}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Role
            </dt>
            <dd className="mt-1 text-gray-900">{role}</dd>
          </div>
          {email && (
            <div className="sm:col-span-2">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Email
              </dt>
              <dd className="mt-1 break-all text-gray-900">{email}</dd>
            </div>
          )}
        </dl>
      </motion.div>

      <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <KeyIcon className="h-5 w-5 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">Change password</h2>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Choose something you will remember: passwords cannot be reset by email on
          this site yet, so a forgotten one has to be changed for you by an
          administrator.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label
              htmlFor="new-password"
              className="block text-sm font-medium text-gray-700"
            >
              New password
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium text-gray-700"
            >
              Repeat it
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {notice && (
            <div
              className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${
                notice.ok
                  ? "border border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border border-red-200 bg-red-50 text-red-700"
              }`}
            >
              {notice.ok ? (
                <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <span>{notice.text}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save new password"}
          </button>
        </form>
      </div>

      <button
        onClick={signOut}
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-red-600"
      >
        <ArrowRightOnRectangleIcon className="h-4 w-4" />
        Sign out
      </button>
    </div>
  );
};

export default Profile;
